import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ExternalLink {
  title: string;
  url: string;
}

export interface AudienceDemographics {
  countries?: Array<{ country: string; percentage: number }>;
}

export interface Creator {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  follower_count: number;
  following_count: number;
  video_count: number;
  like_count: number;
  platform: 'tiktok' | 'instagram' | 'youtube';
  is_verified: boolean;
  bio: string;
  // Enhanced profile fields
  external_links?: ExternalLink[];
  audience_demographics?: AudienceDemographics;
  category?: string;
  region?: string;
  email?: string;
  contact_phone?: string;
  business_category?: string;
  profile_url?: string;
}

interface UseCreatorsProps {
  platform: 'tiktok' | 'instagram' | 'youtube';
  query: string;
  cursor?: string;
  enabled?: boolean;
}

interface CreatorsResponse {
  creators: Creator[];
  cursor: string | null;
  platform: string;
}

export const useCreators = ({ platform, query, cursor, enabled = true }: UseCreatorsProps) => {
  return useQuery({
    queryKey: ['creators', platform, query, cursor],
    queryFn: async (): Promise<CreatorsResponse> => {
      if (!query || query.trim().length === 0) {
        return { creators: [], cursor: null, platform };
      }

      const { data, error } = await supabase.functions.invoke('search-creators', {
        body: { platform, query, cursor },
      });

      if (error) {
        console.error('Error fetching creators:', error);
        throw error;
      }

      return data as CreatorsResponse;
    },
    enabled: enabled && query.trim().length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
